/**
 * Pure planners for a focus of several frames (plan 13), whoever opened
 * them: the main frame large, the others in a column to its right
 * (`focus-round-boxes.ts`). `frameIds` holds the main frame first, then the
 * column top to bottom; activating a frame never reorders it. The main
 * changes only on purpose: a new focus, an add (the new frame is main, the
 * previous main tops the column) or "Show large". Closing the main makes
 * the column's top the main; closing a side frame keeps the main where it
 * is and closes the column's gap. Windows the user placed keep their boxes.
 *
 * An agent's presentation's owner is the stream whose answers replace each
 * other: its conversation, else its task or execution (the
 * backend's `owner_id`). One execution is one round. A request joins when
 * its owner already shows frames of that execution (unless it replaces the
 * round: a guide step's `show_only`), `keep` adds, and anything else
 * replaces that owner's earlier frames; other owners' frames
 * and the user's stay. Frames the user opened, moved or resized are the
 * user's. At the cap of four an agent's round drops the owner's earlier
 * round first, then other agent frames, then the oldest; the user's add
 * drops the oldest side frame. Never the main.
 */
import {
  calculateFocusWindowBox,
  clampBoxToSurface,
  MAX_FOCUS_FRAMES,
  type FocusWindowBox,
  type FocusWindowMode,
} from "@/lib/focus-window-layout";
import type { FocusOriginalSize } from "@/lib/focus/focus-container-boxes";
import { calculateRoundGroupBoxes } from "@/lib/focus/focus-round-boxes";
import type {
  CustomFocusLayout,
  FocusFrameTag,
  PresentationState,
} from "@/lib/focus/focus-types";
import {
  focusBoxesMatch,
  isConversationFrame,
  naturalSizeIn,
  planFocusBoxes,
  planFocusWindow,
  type FocusPlanEnv,
} from "@/lib/focus/focus-plan-layout";

/** The owner and round a presentation belongs to; null when the user opens. */
export interface FocusRoundOwner {
  ownerId: string;
  round: string | null;
}

export interface FocusRoundInput {
  /** The presented frames, the main one first. */
  frameIds: string[];
  owner: FocusRoundOwner | null;
  /** `visibility: "keep"`: add beside what is on screen. */
  keep: boolean;
  /** A guide step's `show_only`: the owner's earlier frames close, its round's too. */
  replacesRound?: boolean;
  /** The content size the agent asked for its main frame. */
  desired?: { width: number; height: number };
  origin?: PresentationState["origin"];
}

type RoundsLayout = Extract<CustomFocusLayout, { kind: "rounds" }>;

/** Whether the user owns a focused frame: opened, moved or resized it. */
export function isUserKept(tag: FocusFrameTag | undefined): boolean {
  return !tag?.owner || tag.kept === "user";
}

function tagOf(state: PresentationState, frameId: string) {
  return state.frameTags?.[frameId];
}

function roundsLayout(state: PresentationState): RoundsLayout | null {
  const layout = state.customFocusLayout;
  return layout?.kind === "rounds" ? layout : null;
}

/** Rounds hold non-chat frames, in a single window or a rounds layout. */
function roundsApply(
  state: PresentationState,
  frameIds: string[],
  env: FocusPlanEnv,
): boolean {
  return (
    state.variant === "focus" &&
    (!state.customFocusLayout || !!roundsLayout(state)) &&
    [...state.frameIds, ...frameIds].every(
      (id) => env.frameType(id) === undefined || !isConversationFrame(env, id),
    )
  );
}

/** Whether `ownerId` shows this frame and the user has not taken it. */
export function isAgentFrame(
  state: PresentationState,
  frameId: string,
  ownerId: string | undefined,
): boolean {
  const tag = tagOf(state, frameId);
  return !isUserKept(tag) && tag?.owner === ownerId;
}

/** The owner already shows frames of this execution: the request joins. */
function joinsRound(state: PresentationState, owner: FocusRoundOwner | null) {
  return (
    !!owner?.round &&
    state.frameIds.some((id) => {
      const tag = tagOf(state, id);
      return tag?.owner === owner.ownerId && tag.round === owner.round;
    })
  );
}

function evictOverCap(
  frameIds: string[],
  tags: Record<string, FocusFrameTag>,
  owner: FocusRoundOwner | null,
): string[] {
  const ids = [...frameIds];
  const earlierRound = (id: string) =>
    !isUserKept(tags[id]) &&
    tags[id]?.owner === owner?.ownerId &&
    tags[id]?.round !== owner?.round;
  while (ids.length > MAX_FOCUS_FRAMES) {
    // Oldest first: the column's bottom; never the main. The user's add
    // drops the oldest; an agent's round drops agent frames first.
    const candidates = ids.slice(1).reverse();
    const victim =
      (owner &&
        (candidates.find(earlierRound) ??
          candidates.find((id) => !isUserKept(tags[id])))) ||
      candidates[0]!;
    ids.splice(ids.indexOf(victim), 1);
  }
  return ids;
}

/**
 * The frames a round keeps from the running focus, in their order. Empty
 * when the round starts the focus over.
 */
export function framesKeptByRound(
  state: PresentationState,
  input: Pick<FocusRoundInput, "frameIds" | "owner" | "keep" | "replacesRound">,
  env: FocusPlanEnv,
): string[] {
  if (state.variant !== "focus") return [];
  const requested = new Set(input.frameIds);
  const replaces =
    !input.keep && (!!input.replacesRound || !joinsRound(state, input.owner));
  return state.frameIds.filter(
    (id) =>
      env.frameType(id) !== undefined &&
      !requested.has(id) &&
      !(replaces && isAgentFrame(state, id, input.owner?.ownerId)),
  );
}

/**
 * Lay the frames out: the main frame large, the column beside it, placed
 * windows where the user put them. A main frame that stays at its size in
 * the same surface keeps its slot.
 */
export function planRoundLayout(
  state: PresentationState,
  env: FocusPlanEnv,
): PresentationState | null {
  const mainId = state.frameIds[0];
  if (state.variant !== "focus" || !mainId) return null;
  const tags = state.frameTags ?? {};
  const layout = roundsLayout(state);
  const desired = layout?.desired ?? {};
  // The user's box is kept as placed and shown inside the current surface,
  // so the window grows back when the display does.
  const placedBox = (id: string) => {
    const box = tags[id]?.placed;
    return box && clampBoxToSurface(box, env.surface);
  };
  const side = state.frameIds.slice(1).filter((id) => !placedBox(id));
  const naturalSizes = Object.fromEntries(
    state.frameIds.map((id) => [id, naturalSizeIn(state, id, env)]),
  );
  const sizeOf = (id: string): FocusOriginalSize => {
    const asked = desired[id];
    if (asked) return { ...asked, container: "object" };
    return env.roundSize?.(id) ?? naturalSizes[id]!;
  };
  const mainSize = sizeOf(mainId);
  const kept = layout?.main;
  const anchor =
    kept?.frameId === mainId &&
    kept.size.width === mainSize.width &&
    kept.size.height === mainSize.height &&
    !!state.surface &&
    focusBoxesMatch(state.surface, env.surface)
      ? kept.box
      : null;
  const [slot, ...column] = calculateRoundGroupBoxes(
    env.surface,
    mainSize,
    side.map(sizeOf),
    anchor,
  );
  const presented = new Set(state.frameIds);
  // A maximized or snapped window stays so, over its place in the group.
  const window = state.focusWindow;
  const keptWindow =
    window &&
    presented.has(window.frameId) &&
    (placedBox(window.frameId) || window.mode !== "normal")
      ? window
      : undefined;
  const windowBox = (id: string) =>
    keptWindow?.frameId === id && keptWindow.mode !== "normal"
      ? calculateFocusWindowBox(keptWindow.mode, env.surface)
      : undefined;
  const columnBoxes = new Map(side.map((id, index) => [id, column[index]!]));
  const boxes = state.frameIds.map(
    (id) => windowBox(id) ?? columnBoxes.get(id) ?? placedBox(id) ?? slot!,
  );
  return {
    ...state,
    boxes,
    naturalSizes,
    surface: env.surface,
    focusWindow: keptWindow,
    agentContentFit: undefined,
    frameTags: Object.fromEntries(
      Object.entries(tags).filter(([id]) => presented.has(id)),
    ),
    customFocusLayout: {
      kind: "rounds",
      boxes,
      desired: Object.fromEntries(
        Object.entries(desired).filter(([id]) => presented.has(id)),
      ),
      main: {
        frameId: mainId,
        box: slot!,
        size: { width: mainSize.width, height: mainSize.height },
      },
    },
  };
}

/**
 * Lay a focus out: in the boxes given (a chat's columns or stack, an
 * agent's content fit), else one frame as a window and several as rounds.
 */
export function planFocusLayout(
  state: PresentationState,
  activeId: string,
  options: {
    boxes?: FocusWindowBox[];
    focusWindowMode?: FocusWindowMode | null;
    preserveCustomFocusLayout?: boolean;
  },
  env: FocusPlanEnv,
): PresentationState | null {
  if (state.variant !== "focus") return null;
  const { boxes } = options;
  if (boxes && boxes.length >= state.frameIds.length) {
    return planFocusBoxes(state, activeId, { ...options, boxes }, env);
  }
  if (state.frameIds.length <= 1) {
    return planFocusWindow(state, activeId, "normal", {}, env);
  }
  return planRoundLayout(state, env);
}

/** Tags for a round's frames; a frame already the user's stays theirs. */
function roundTags(
  state: PresentationState,
  requested: string[],
  owner: FocusRoundOwner | null,
): Record<string, FocusFrameTag> {
  const tags: Record<string, FocusFrameTag> = { ...state.frameTags };
  for (const id of requested) {
    const previous = state.frameIds.includes(id) ? tags[id] : undefined;
    if (!owner) tags[id] = { ...previous, kept: "user" };
    else if (!isUserFrame(state, id)) {
      tags[id] = { ...previous, owner: owner.ownerId, round: owner.round };
    }
  }
  return tags;
}

/** A presented frame without an agent's tag: the user opened it. */
function isUserFrame(state: PresentationState, frameId: string) {
  return state.frameIds.includes(frameId) && isUserKept(tagOf(state, frameId));
}

/** Present a round into the running focus: its first frame is the main. */
export function planFocusRound(
  state: PresentationState,
  input: FocusRoundInput,
  env: FocusPlanEnv,
): PresentationState | null {
  const requested = [...new Set(input.frameIds)].filter(
    (id) => env.frameType(id) !== undefined,
  );
  const mainId = requested[0];
  if (!mainId || !roundsApply(state, requested, env)) return null;
  const tags = roundTags(state, requested, input.owner);
  const frameIds = evictOverCap(
    [...requested, ...framesKeptByRound(state, input, env)],
    tags,
    input.owner,
  );
  // A request without a size drops the size an earlier one asked for.
  const { [mainId]: _previous, ...desired } =
    roundsLayout(state)?.desired ?? {};
  if (input.desired) desired[mainId] = input.desired;
  return planRoundLayout(
    {
      ...state,
      frameIds,
      activeIndex: 0,
      origin: input.owner ? input.origin : state.origin,
      frameTags: tags,
      // A maximized or snapped window makes way for the new main frame.
      focusWindow:
        state.focusWindow?.mode === "normal" ? state.focusWindow : undefined,
      customFocusLayout: {
        kind: "rounds",
        boxes: [],
        desired,
        main: roundsLayout(state)?.main,
      },
    },
    env,
  );
}

/**
 * "Show large": the user makes a presented frame the main one. It leaves
 * its place in the column (or where the user put it) and becomes the
 * user's; the previous main tops the column. Null when it already is.
 */
export function planMakeMainFocusFrame(
  state: PresentationState,
  frameId: string,
  env: FocusPlanEnv,
): PresentationState | null {
  const index = state.frameIds.indexOf(frameId);
  const { placed, ...tag } = tagOf(state, frameId) ?? {};
  if (index < 0 || (index === 0 && !placed)) return null;
  if (!roundsApply(state, [], env)) return null;
  const frameIds = [frameId, ...state.frameIds.filter((id) => id !== frameId)];
  return planRoundLayout(
    {
      ...state,
      frameIds,
      activeIndex: 0,
      frameTags: { ...state.frameTags, [frameId]: { ...tag, kept: "user" } },
    },
    env,
  );
}

/** The user moved or resized a window: it keeps that box from now on. */
export function planPlaceRoundFrame(
  state: PresentationState,
  frameId: string,
  box: FocusWindowBox,
  env: FocusPlanEnv,
): PresentationState | null {
  if (state.variant !== "focus" || !state.frameIds.includes(frameId)) {
    return null;
  }
  const kept = withUserKept(state, frameId, box);
  return planRoundLayout(
    {
      ...kept,
      focusWindow:
        state.focusWindow?.frameId === frameId ? undefined : state.focusWindow,
    },
    env,
  );
}

/** Maximize a frame, or put a maximized or snapped one back in its place. */
export function planToggleFocusWindow(
  state: PresentationState,
  frameId: string,
  env: FocusPlanEnv,
): PresentationState | null {
  const window = state.focusWindow;
  const restores = window?.frameId === frameId && window.mode !== "normal";
  if (restores && roundsLayout(state)) {
    return planRoundLayout({ ...state, focusWindow: undefined }, env);
  }
  return planFocusWindow(
    state,
    frameId,
    restores ? "normal" : "maximized",
    {},
    env,
  );
}

/** Mark a frame the user's: opened by them, or placed at `placed`. */
export function withUserKept(
  state: PresentationState,
  frameId: string,
  placed?: FocusWindowBox,
): PresentationState {
  const previous = state.frameTags?.[frameId];
  if (!state.frameIds.includes(frameId)) return state;
  // An untagged frame is already the user's.
  if (isUserKept(previous) && !placed) return state;
  return {
    ...state,
    frameTags: {
      ...state.frameTags,
      [frameId]: { ...previous, kept: "user", ...(placed && { placed }) },
    },
  };
}

/** An agent's `clear` of frames: its owner's frames leave, all others stay. */
export function planClearAgentFrames(
  state: PresentationState,
  ownerId: string,
  env: FocusPlanEnv,
): { kind: "exit" } | { kind: "change"; next: PresentationState } | null {
  if (state.variant !== "focus") return null;
  const remaining = state.frameIds.filter(
    (id) => !isAgentFrame(state, id, ownerId),
  );
  if (remaining.length === state.frameIds.length) return null;
  if (remaining.length === 0) return { kind: "exit" };
  const previousActive = state.frameIds[state.activeIndex];
  const next = planRoundLayout(
    {
      ...state,
      frameIds: remaining,
      activeIndex: Math.max(0, remaining.indexOf(previousActive ?? "")),
    },
    env,
  );
  return next && { kind: "change", next };
}
